import { ApiKeyControllerApi } from '@clients/apikeymanager';
import * as backendApis from '@clients/backend/api';
import { type Configuration } from '@clients/backend/configuration';
import {
  CompanyRolesControllerApi,
  type CompanyRolesControllerApiInterface,
  EmailAddressControllerApi,
  InheritedRolesControllerApi,
  CompanyRightsControllerApi,
} from '@clients/communitymanager';
import {
  DataSourcingControllerApi,
  EnhancedRequestControllerApi,
  RequestControllerApi,
} from '@clients/datasourcingservice';
import { DocumentControllerApi } from '@clients/documentmanager';
import { EmailControllerApi } from '@clients/emailservice';
import { QaControllerApi, DatasetJudgementControllerApi, PreApprovalControllerApi } from '@clients/qaservice';
import { PortfolioControllerApi } from '@clients/userservice';
import type Keycloak from 'keycloak-js';
import axios, { type AxiosInstance } from 'axios';
import { updateTokenAndItsExpiryTimestampAndStoreBoth } from '@/utils/SessionTimeoutUtils';
import { DataPointControllerApi, DataExportControllerApi } from '@clients/backend/api';
import { CreditsControllerApi } from '@clients/accountingservice';

interface ApiBackendClients {
  actuator: backendApis.ActuatorApiInterface;
  companyDataController: backendApis.CompanyDataControllerApiInterface;
  dataAvailabilityController: backendApis.DataAvailabilityControllerApiInterface;
  metaDataController: backendApis.MetaDataControllerApiInterface;
  userUploadsController: backendApis.UserUploadsControllerApiInterface;
  nonSourceabilityController: backendApis.NonSourceabilityControllerApiInterface;
}

interface ApiClients {
  apiKeyController: ApiKeyControllerApi;
  documentController: DocumentControllerApi;
  requestController: RequestControllerApi;
  dataSourcingController: DataSourcingControllerApi;
  enhancedRequestController: EnhancedRequestControllerApi;
  companyRolesController: CompanyRolesControllerApiInterface;
  inheritedRolesController: InheritedRolesControllerApi;
  qaController: QaControllerApi;
  datasetJudgementController: DatasetJudgementControllerApi;
  emailController: EmailControllerApi;
  portfolioController: PortfolioControllerApi;
  emailAddressController: EmailAddressControllerApi;
  dataPointController: DataPointControllerApi;
  dataExportController: DataExportControllerApi;
  companyRightsController: CompanyRightsControllerApi;
  creditsController: CreditsControllerApi;
  preApprovalController: PreApprovalControllerApi;
}

type ApiClientConstructor<T> = new (
  configuration: Configuration | undefined,
  basePath: string,
  axios: AxiosInstance
) => T;
type ApiClientFactory = <T>(constructor: ApiClientConstructor<T>) => T;

export class ApiClientProvider {
  private readonly keycloakPromise: Promise<Keycloak>;
  readonly axiosInstance: AxiosInstance;

  readonly backendClients: ApiBackendClients;
  readonly apiClients: ApiClients;

  constructor(keycloakPromise: Promise<Keycloak>) {
    this.keycloakPromise = keycloakPromise;
    this.axiosInstance = axios.create({});
    this.registerAutoAuthenticatingAxiosInterceptor();

    this.backendClients = this.constructBackendClients();
    this.apiClients = this.constructApiClients();
  }

  private registerAutoAuthenticatingAxiosInterceptor(): void {
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        const bearerToken = await this.getBearerToken();
        if (bearerToken) {
          config.headers['Authorization'] = `Bearer ${bearerToken}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(new Error(error.message ?? 'Unknown error occurred'));
      }
    );
  }

  private constructBackendClients(): ApiBackendClients {
    const backendClientFactory = this.getClientFactory('/api');
    return {
      actuator: backendClientFactory(backendApis.ActuatorApi),
      companyDataController: backendClientFactory(backendApis.CompanyDataControllerApi),
      dataAvailabilityController: backendClientFactory(backendApis.DataAvailabilityControllerApi),
      metaDataController: backendClientFactory(backendApis.MetaDataControllerApi),
      userUploadsController: backendClientFactory(backendApis.UserUploadsControllerApi),
      nonSourceabilityController: backendClientFactory(backendApis.NonSourceabilityControllerApi),
    };
  }

  private constructApiClients(): ApiClients {
    return {
      apiKeyController: this.getClientFactory('/api-keys')(ApiKeyControllerApi),
      documentController: this.getClientFactory('/documents')(DocumentControllerApi),
      requestController: this.getClientFactory('/data-sourcing')(RequestControllerApi),
      dataSourcingController: this.getClientFactory('/data-sourcing')(DataSourcingControllerApi),
      enhancedRequestController: this.getClientFactory('/data-sourcing')(EnhancedRequestControllerApi),
      companyRolesController: this.getClientFactory('/community')(CompanyRolesControllerApi),
      inheritedRolesController: this.getClientFactory('/community')(InheritedRolesControllerApi),
      qaController: this.getClientFactory('/qa')(QaControllerApi),
      datasetJudgementController: this.getClientFactory('/qa')(DatasetJudgementControllerApi),
      emailController: this.getClientFactory('/email')(EmailControllerApi),
      portfolioController: this.getClientFactory('/users')(PortfolioControllerApi),
      emailAddressController: this.getClientFactory('/community')(EmailAddressControllerApi),
      dataPointController: this.getClientFactory('/api')(DataPointControllerApi),
      dataExportController: this.getClientFactory('/api')(DataExportControllerApi),
      companyRightsController: this.getClientFactory('/community')(CompanyRightsControllerApi),
      creditsController: this.getClientFactory('/accounting')(CreditsControllerApi),
      preApprovalController: this.getClientFactory('/qa')(PreApprovalControllerApi),
    };
  }

  private async getBearerToken(): Promise<string | undefined> {
    const keycloak = await this.keycloakPromise;
    if (keycloak.authenticated) {
      await updateTokenAndItsExpiryTimestampAndStoreBoth(keycloak);
      return keycloak.token;
    } else {
      return undefined;
    }
  }

  private getClientFactory(basePath: string): ApiClientFactory {
    return (constructor) => {
      return new constructor(undefined, basePath, this.axiosInstance);
    };
  }
}
